import { useCustomRouter } from '@/root/shared-hooks'

/**
 * Resolves a locale-suffixed route attribute value, falling back when it is absent.
 * Attribute keys are stored uppercase-suffixed per locale, e.g. `ONLINE_SOLD_OUT_INFO_PART_ONE_ET`.
 * Route attributes can also hold booleans; only string values are returned, anything else falls back.
 */
export const getRouteAttributeText = (
  routeAttributes: Record<string, boolean | string> | undefined,
  locale: string,
  attributeKey: string,
  fallback: string
): string => {
  const value = routeAttributes?.[`${attributeKey}_${locale.toUpperCase()}`]

  return typeof value === 'string' ? value : fallback
}

/**
 * Hook that injects the current locale, returning a getter over route attributes.
 * Example: `attributeKey='ONLINE_SOLD_OUT_INFO_PART_ONE'` -> looks for `ONLINE_SOLD_OUT_INFO_PART_ONE_ET`.
 */
export const useRouteAttributeText = (routeAttributes?: Record<string, boolean | string>) => {
  const { locale } = useCustomRouter()

  return (attributeKey: string, fallback: string): string =>
    getRouteAttributeText(routeAttributes, locale, attributeKey, fallback)
}

/**
 * Builds the sold-out notice from the two `ONLINE_SOLD_OUT_INFO_PART_{ONE,TWO}` route attributes.
 * Part one falls back to `fallback` (a translation); part two is optional. Present parts are joined:
 * both → "part one part two", first only → "part one", neither → `fallback`.
 */
export const getSoldOutText = (
  getAttributeText: (attributeKey: string, fallback: string) => string,
  fallback: string
): string =>
  [getAttributeText('ONLINE_SOLD_OUT_INFO_PART_ONE', fallback), getAttributeText('ONLINE_SOLD_OUT_INFO_PART_TWO', '')]
    .filter(Boolean)
    .join(' ')
